import { useState } from 'react';
import { WifiOff } from 'lucide-react';
import { OfflineArticleCard } from '@/components/OfflineArticleCard';
import { OfflinePublicationCard } from '@/components/OfflinePublicationCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useOfflineLibrary } from '@/hooks/useOfflineLibrary';
import { useOfflinePublications } from '@/hooks/useOfflinePublications';

/**
 * The "Saved for offline" section of the profile page: every wiki
 * article and NKBIP-01 publication the user has downloaded to this
 * browser for reading without a connection (see `useOfflineLibrary` and
 * `useOfflinePublications`).
 *
 * This is intentionally device-local rather than per-account: the
 * library lives in this browser's `localStorage`, is never published to
 * Nostr, and so isn't tied to any particular Nostr identity. That's
 * why it's only rendered on the logged-in user's *own* profile — see
 * `ProfilePage`.
 */
export function OfflineLibrarySection() {
  const { articles, count: articleCount, clear: clearArticles } = useOfflineLibrary();
  const { publications, count: publicationCount, clear: clearPublications } = useOfflinePublications();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const total = articleCount + publicationCount;

  return (
    <>
      <div className="mb-4 mt-10 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-bold tracking-tight">
          <WifiOff className="size-5" />
          Saved for offline
          {total > 0 && <span className="text-base font-normal text-muted-foreground">({total})</span>}
        </h2>
        {total > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            Remove all
          </Button>
        )}
      </div>

      {total === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 px-8 text-center">
            <p className="mx-auto max-w-md text-muted-foreground">
              Nothing saved yet. Use <span className="font-medium text-foreground">Save</span> on any article or
              publication to keep a copy in this browser — it'll stay readable even with no connection, which pairs well
              with <span className="font-medium text-foreground">Reader</span> mode on an e-reader or tablet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {publicationCount > 0 && (
            <section>
              <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                Publications ({publicationCount})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {publications.map((publication) => (
                  <OfflinePublicationCard key={publication.index.id} publication={publication} />
                ))}
              </div>
            </section>
          )}

          {articleCount > 0 && (
            <section>
              <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Articles ({articleCount})</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {articles.map((article) => (
                  <OfflineArticleCard key={article.event.id} article={article} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove everything saved offline?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears all {total} item{total === 1 ? '' : 's'} saved in this browser for offline reading. The
              articles and publications themselves stay on Nostr — you can save them again any time you're online.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                clearArticles();
                clearPublications();
                setConfirmOpen(false);
              }}
            >
              Remove all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
